import React, { useState, useEffect } from 'react';
import { Modal, Spin, Button, Tabs } from 'antd';
import { DownloadOutlined, CopyOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownViewer = ({ visible, onClose, markdownContent, reportId, reportName }) => {
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('preview');

    const handleCopy = () => {
        navigator.clipboard.writeText(markdownContent).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDownload = () => {
        const BOM = "\uFEFF";
        const fullContent = BOM + markdownContent;
        const blob = new Blob([fullContent], { type: 'text/markdown;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportId}_${reportName}.md`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Modal
            title={`Markdown 미리보기 - ${reportName}`}
            open={visible}
            onCancel={onClose}
            width="80%"
            style={{ top: 20 }}
            bodyStyle={{ height: '70vh', overflow: 'hidden' }}
            footer={[
                <Button key="copy" icon={<CopyOutlined />} onClick={handleCopy}>
                    {copied ? '복사됨!' : '복사'}
                </Button>,
                <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                    다운로드
                </Button>,
                <Button key="close" onClick={onClose}>
                    닫기
                </Button>,
            ]}
        >
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <Tabs.TabPane 
                    tab={<span><EyeOutlined /> 미리보기</span>} 
                    key="preview"
                >
                    <div style={{ 
                        padding: '20px',
                        backgroundColor: '#fff',
                        maxHeight: '60vh',
                        overflowY: 'auto'
                    }}>
                        <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            components={{
                                h1: ({children}) => <h1 style={{fontSize: '2em', marginTop: '0.67em', marginBottom: '0.67em', fontWeight: 'bold'}}>{children}</h1>,
                                h2: ({children}) => <h2 style={{fontSize: '1.5em', marginTop: '0.83em', marginBottom: '0.83em', fontWeight: 'bold'}}>{children}</h2>,
                                h3: ({children}) => <h3 style={{fontSize: '1.17em', marginTop: '1em', marginBottom: '1em', fontWeight: 'bold'}}>{children}</h3>,
                                table: ({children}) => <table style={{borderCollapse: 'collapse', width: '100%', marginBottom: '1em'}}>{children}</table>,
                                thead: ({children}) => <thead>{children}</thead>,
                                tbody: ({children}) => <tbody>{children}</tbody>,
                                tr: ({children}) => <tr>{children}</tr>,
                                th: ({children}) => <th style={{border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', textAlign: 'left'}}>{children}</th>,
                                td: ({children}) => <td style={{border: '1px solid #ddd', padding: '8px'}}>{children}</td>,
                                p: ({children}) => <p style={{marginBottom: '1em'}}>{children}</p>,
                                ul: ({children}) => <ul style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ul>,
                                ol: ({children}) => <ol style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ol>,
                                li: ({children}) => <li style={{marginBottom: '0.5em'}}>{children}</li>,
                                code: ({inline, children}) => 
                                    inline ? 
                                    <code style={{backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '0.9em'}}>{children}</code> :
                                    <pre style={{backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', overflow: 'auto', marginBottom: '1em'}}>
                                        <code style={{fontSize: '0.9em'}}>{children}</code>
                                    </pre>,
                                blockquote: ({children}) => <blockquote style={{borderLeft: '4px solid #ddd', marginLeft: 0, paddingLeft: '1em', color: '#666', marginBottom: '1em'}}>{children}</blockquote>,
                                hr: () => <hr style={{border: 'none', borderTop: '1px solid #ddd', margin: '2em 0'}} />,
                                strong: ({children}) => <strong style={{fontWeight: 'bold'}}>{children}</strong>,
                                em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
                                a: ({href, children}) => <a href={href} style={{color: '#0066cc', textDecoration: 'underline'}} target="_blank" rel="noopener noreferrer">{children}</a>
                            }}
                        >
                            {markdownContent}
                        </ReactMarkdown>
                    </div>
                </Tabs.TabPane>
                <Tabs.TabPane 
                    tab={<span><FileTextOutlined /> 원본</span>} 
                    key="source"
                >
                    <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '20px', 
                        borderRadius: '4px',
                        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        maxHeight: '60vh',
                        overflowY: 'auto'
                    }}>
                        {markdownContent}
                    </pre>
                </Tabs.TabPane>
            </Tabs>
        </Modal>
    );
};

export default MarkdownViewer;